import React, { useEffect, useState } from 'react';
import { applyTypography, BaseButton, useT } from '@repo/common';

const DEFAULT_AVATAR = `${process.env.REACT_APP_ASSETS_URL}/img-profile.svg`;

const WORKSHOP_REVIEW_FIXTURES = {
  'Gyeongbokgung Palace Hanbok Photoshoot & Tour': {
    quoteLines: [
      'Wearing hanbok inside Gyeongbokgung made the palace feel completely different.',
      'The host helped with poses, hidden photo spots, and the photos came out beautifully.',
    ],
    name: 'Mia',
    age: '26',
    country: 'United States',
    countryIcon: '🇺🇸',
  },
  'Traditional Korean Cooking Class (Bibimbap & Bulgogi)': {
    quoteLines: [
      'I finally learned how Korean home cooking actually works, not just the recipe.',
      'The class was relaxed, hands-on, and eating together at the end was the best part.',
    ],
    name: 'Lucas',
    age: '28',
    country: 'France',
    countryIcon: '🇫🇷',
  },
  'Free K-Pop Dance One-day Class for Beginners': {
    quoteLines: [
      'I was nervous because I had never danced before, but the instructor made it easy.',
      'By the end, everyone was laughing, filming each other, and cheering like friends.',
    ],
    name: 'Aira',
    age: '24',
    country: 'Thailand',
    countryIcon: '🇹🇭',
  },
  'Seoul Han River Night View Cruise Tour (Sold Out)': {
    quoteLines: [
      'The night view from the river was the Seoul moment I wanted from this trip.',
      'It was simple to join, the timing was perfect, and the city lights were amazing.',
    ],
    name: 'Noah',
    age: '30',
    country: 'Canada',
    countryIcon: '🇨🇦',
  },
  'Seoul Traditional Tea Ceremony Experience': {
    quoteLines: [
      'This was quiet, slow, and really meaningful after a busy week in Seoul.',
      'The tea master explained every gesture, so I understood the culture behind it.',
    ],
    name: 'Hannah',
    age: '29',
    country: 'Australia',
    countryIcon: '🇦🇺',
  },
};

const FALLBACK_REVIEWS = Object.values(WORKSHOP_REVIEW_FIXTURES);

const buildWorkshopReviews = (workshops) => {
  const matchedReviews = workshops
    .map((workshop) => WORKSHOP_REVIEW_FIXTURES[workshop.title])
    .filter(Boolean);

  return (matchedReviews.length > 0 ? matchedReviews : FALLBACK_REVIEWS).map(
    (review) => ({
      ...review,
      avatar: review.avatar || DEFAULT_AVATAR,
    }),
  );
};

const ReviewSection = ({ workshops = [] }) => {
  const t = useT();
  const [currentSlide, setCurrentSlide] = useState(0);
  const reviews = buildWorkshopReviews(workshops);

  useEffect(() => {
    setCurrentSlide((slide) => Math.min(slide, Math.max(reviews.length - 1, 0)));
  }, [reviews.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  return (
    <section className='flex w-full flex-col items-center overflow-hidden bg-grayscale-gray00 px-20 py-10 text-center desktopL:min-h-[420px] desktopL:py-[50px]'>
      <div className='relative flex w-full min-w-0 flex-col items-center overflow-hidden pb-6 desktopL:min-h-[299px] desktopL:pb-10'>
        <div
          className='flex w-full min-w-0 transition-transform duration-500 ease-in-out'
          style={{
            transform: `translateX(-${currentSlide * 100}%)`,
          }}
        >
          {reviews.map((review) => (
            <div
              key={`${review.name}-${review.country}`}
              className='flex w-full min-w-0 flex-none flex-col items-center'
            >
              <div className='flex flex-col items-center pb-2 desktopL:pb-3'>
                <p
                  className={`${applyTypography(
                    'b14Strong',
                  )} leading-[1.4] text-green-deep01 desktopL:typo-b16Strong desktopL:leading-[1.5]`}
                >
                  {t('user review', '이용 후기')}
                </p>
              </div>
              <div className='flex w-full items-center justify-center pb-6 desktopL:pb-[30px]'>
                <div className='flex w-full min-w-0 max-w-[1760px] flex-col justify-center text-center leading-[1.2] desktopL:min-h-[92px]'>
                  <p
                    className={`${applyTypography(
                      'h7',
                    )} mb-0 w-full break-words text-green-deep05 desktopL:typo-h4`}
                  >
                    {review.quoteLines[0]}
                  </p>
                  <p className='w-full break-words typo-h7 text-green-deep05 desktopL:typo-h4'>
                    {review.quoteLines[1]}
                  </p>
                </div>
              </div>
              <div className='flex flex-col items-center gap-2 desktopL:gap-3'>
                <div className='relative rounded-full size-[60px]'>
                  <img
                    src={review.avatar}
                    alt={t(`${review.name} avatar`, `${review.name} 프로필 이미지`)}
                    loading='lazy'
                    decoding='async'
                    className='absolute inset-0 object-cover object-center rounded-full pointer-events-none size-full'
                  />
                </div>
                <div className='flex items-center justify-center gap-1 leading-[1.5] text-green-deep03 desktopL:gap-2 desktopL:leading-[1.2]'>
                  <p
                    className={`${applyTypography(
                      'b16Strong',
                    )} text-green-deep03 desktopL:typo-b20Strong`}
                  >
                    {review.name}
                  </p>
                  <p
                    className={`${applyTypography(
                      'b16Strong',
                    )} text-green-deep03 desktopL:typo-b20Strong`}
                  >
                    {review.age}
                  </p>
                  <span
                    className='text-[18px] leading-none desktopL:text-[24px]'
                    style={{
                      fontFamily:
                        '"Twemoji Country Flags", "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif',
                    }}
                    role='img'
                    aria-label={review.country}
                  >
                    {review.countryIcon}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className='flex items-center justify-center'>
        {reviews.map((_, idx) => {
          const isActive = idx === currentSlide;
          return (
            <BaseButton
              key={idx}
              type='text'
              size='s'
              isStatic
              onClick={() => goToSlide(idx)}
              aria-label={t(`Go to review ${idx + 1}`, `${idx + 1}번 후기로 이동`)}
              className={`h-auto min-w-0 cursor-pointer border-0 bg-transparent px-[6px] py-[8px] ${
                isActive ? '[&>span]:flex-col w-[36px]' : ''
              }`}
              icon={
                <div
                  className={`rounded-[12px] ${
                    isActive
                      ? 'bg-grayscale-gray07 h-[8px] w-full'
                      : 'bg-grayscale-gray03 size-[8px]'
                  }`}
                />
              }
            />
          );
        })}
      </div>
    </section>
  );
};

export default ReviewSection;
